import { useState, useEffect, useRef } from "react";
import { HelpCircle, BookOpen, MessageSquarePlus } from "lucide-react";
import { useLocation } from "react-router-dom";
import { QuickGuide } from "./QuickGuide";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { FeedbackDialog } from "@/components/feedback/FeedbackDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/context/LanguageContext";
import { dashboardLabels } from "@/i18n";
import { getNativeSidePanelButtonClass } from "@/lib/native/nativeSidePanelPosition";
import { useNativePlatform } from "@/hooks/useNativePlatform";

import { cn } from "@/lib/utils";
import { nativeSheetScrollBottomPad } from "@/lib/native/nativeSheetPadding";

const HOVER_CLOSE_DELAY_MS = 150;

interface QuickGuideSidePanelProps {
  showQuickGuide: boolean;
}

export const QuickGuideSidePanel = ({
  showQuickGuide,
}: QuickGuideSidePanelProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const { language } = useLanguage();
  const labels = dashboardLabels(language)?.quickGuide || {};
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isDashboard = location.pathname === "/dashboard";

  useEffect(() => {
    if (isDashboard && showQuickGuide) {
      setIsOpen(true);
    }
  }, [isDashboard, showQuickGuide]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const scheduleClose = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = setTimeout(() => {
      setMenuOpen(false);
      closeTimeoutRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  };

  const cancelClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleTriggerPointerEnter = () => {
    if (isMobile || isNative) return;
    cancelClose();
    setMenuOpen(true);
  };

  const handleTriggerPointerLeave = () => {
    if (isMobile || isNative) return;
    scheduleClose();
  };

  const handleContentPointerEnter = () => {
    if (isMobile || isNative) return;
    cancelClose();
  };

  const handleContentPointerLeave = () => {
    if (isMobile || isNative) return;
    scheduleClose();
  };

  if (!isDashboard) {
    return null;
  }

  const sideButtonClass =
    isNative || isMobile
      ? getNativeSidePanelButtonClass(
          "native-side-panel-btn bg-gradient-to-r from-accent to-accent/50 text-primary-foreground rounded-l-full shadow-2xl hover:shadow-primary/50 transition-all group backdrop-blur-sm border border-primary/20 py-3 pl-3 pr-2",
          { preferMobilePosition: isMobile && !isNative },
        )
      : cn(
          "fixed right-0 z-40 top-[28%] -translate-y-1/2",
          "py-3 pl-3 pr-2 md:py-4 md:pl-4 md:pr-3",
          "bg-gradient-to-r from-accent to-accent/50 text-primary-foreground rounded-l-full shadow-2xl hover:shadow-primary/50 transition-all group backdrop-blur-sm border border-primary/20",
        );

  const menuItems = (
    <>
      <button
        type="button"
        onClick={() => {
          setMenuOpen(false);
          setIsOpen(true);
        }}
        className="flex items-center gap-3 rounded-lg px-4 py-3.5 text-base font-medium text-left hover:bg-accent/20 active:bg-accent/20 transition-colors w-full"
      >
        <BookOpen className="h-5 w-5 text-accent shrink-0" />
        {labels.title || "Quick guide"}
      </button>
      <button
        type="button"
        onClick={() => {
          setMenuOpen(false);
          // Let the option sheet finish closing before opening feedback (avoids nested-sheet glitches on native).
          window.setTimeout(() => setShowFeedbackDialog(true), 320);
        }}
        className="flex items-center gap-3 rounded-lg px-4 py-3.5 text-base font-medium text-left hover:bg-accent/20 active:bg-accent/20 transition-colors w-full"
      >
        <MessageSquarePlus className="h-5 w-5 text-accent shrink-0" />
        {labels.leaveFeedback || "Leave feedback"}
      </button>
    </>
  );

  const triggerButton = (
    <button
      type="button"
      onPointerEnter={handleTriggerPointerEnter}
      onPointerLeave={handleTriggerPointerLeave}
      onClick={isNative || isMobile ? () => setMenuOpen(true) : undefined}
      className={sideButtonClass}
      aria-label="Hjälp och feedback"
      aria-expanded={menuOpen}
    >
      <HelpCircle
        className={cn(
          "text-black",
          isNative ? "w-5 h-5" : "w-5 h-5 md:w-6 md:h-6",
        )}
      />
    </button>
  );

  return (
    <>
      {isNative || isMobile ? (
        <>
          {triggerButton}
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetContent side="bottom" className="native-option-sheet">
              <SheetHeader className="text-left">
                <SheetTitle>{labels.title || "Hjälp"}</SheetTitle>
              </SheetHeader>
              <div
                className={cn(
                  "mt-3 flex flex-col gap-1",
                  nativeSheetScrollBottomPad,
                )}
              >
                {menuItems}
              </div>
            </SheetContent>
          </Sheet>
        </>
      ) : (
        <Popover open={menuOpen} onOpenChange={setMenuOpen}>
          <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
          <PopoverContent
            side="left"
            align="center"
            className="w-52 p-2"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onPointerEnter={handleContentPointerEnter}
            onPointerLeave={handleContentPointerLeave}
          >
            <div className="flex flex-col gap-0.5">{menuItems}</div>
          </PopoverContent>
        </Popover>
      )}

      <QuickGuide
        open={isOpen}
        onOpenChange={setIsOpen}
        showQuickGuide={showQuickGuide}
      />

      <FeedbackDialog
        open={showFeedbackDialog}
        onOpenChange={setShowFeedbackDialog}
      />
    </>
  );
};

export default QuickGuideSidePanel;
